import * as THREE from "three";
import type { QuadTuning, Vec3 } from "./card";
import type { Figure } from "./build";
import type { GroundProbe, Locomotion, DriftReport } from "./motion";

// Procedural motion for a four-legged figure (IMP-0059, gap G13 of dry run 1): the biped motion's sibling for dogs,
// cats, goats, cows and anything else on four legs. No clips, no skinning: the game places the root (position, yaw)
// from its controller and feeds a `QuadLocomotion` each frame; the motion poses the joints.
//
// - Legs are rigid segments solved by two-bone IK: an upper joint (`leg.f.l`, `leg.f.r`, `leg.h.l`, `leg.h.r`) and
//   a lower one under it (`shin.f.l`, …). The knee bends the way the card's rest pose bends it, and the leg keeps its
//   twist (the rest frame is carried, not just the direction).
//   - Two segments: the foot is the point on the ground under the upper joint at rest.
//   - Three segments, an animal's (a card with `foot.<leg>` under the shin and a `toe.<leg>` point on the ground under
//     the foot): the toe is what plants. The foot segment (a pastern, a hock's foot) turns with the step, and the two
//     upper segments reach its top: flat on the ground while planted, peeling up at the end of the stance, folding
//     back as the paw lifts (`fold`: a forepaw tucks back under the leg), straightening to reach before it lands. So
//     a fore leg's elbow sits behind the shoulder and its wrist low; a hind leg's stifle ahead of the hip and its hock
//     behind, as the card's rest pose sets them.
// - The gait is a phase clock: each foot has a phase offset and a duty factor (the share of the cycle it is planted).
//   A walk lands its feet in the lateral sequence (left hind, left fore, right hind, right fore); a trot lands diagonal
//   pairs together; a gallop lands the hind pair, then the fore pair. The gait follows speed (walk, trot, gallop at the
//   card's thresholds), the clock runs at speed over the gait's stride, and the offsets ease between gaits.
// - A planted foot holds its anchor on the probed ground; a swinging foot lands where its hip will be at touchdown plus
//   half the stance's travel, on the probed ground. At a stop, feet in the air finish their swing and plant; standing,
//   a foot left far from its home (after a turn on the spot) steps back under its hip, one at a time.
// - The body pitches and rolls to the ground under its hips, bobs with the gait, breathes at rest; the head looks at a
//   target inside its clamp; a `tail` joint wags; and three poses blend in at rest: eat (the neck and head down), sit
//   (the hind end down, the front up), lie (the body down, the legs folded under it).

/** What the controller tells a four-legged figure each frame: the biped's `Locomotion`, plus a pose at rest and a wag. */
export interface QuadLocomotion extends Pick<Locomotion, "speed" | "velocity" | "lookAt"> {
  /** A pose to settle into while standing (ignored while moving). */
  pose?: "stand" | "eat" | "sit" | "lie";
  /** Wag the tail. */
  wag?: boolean;
}

/** The gaits. */
export type QuadGait = "idle" | "walk" | "trot" | "gallop";

/** Planted defects beside what they break: the gait, drift, knee and pose checks must fail with them on. */
export const QUAD_FAULTS = {
  /** Feet plant at the body's height instead of the probed ground's: planted feet drift on slopes. */
  footProbeOff: false,
  /** The walk's offsets are a pace (each side's pair together): the lateral sequence is lost. */
  walkPaced: false,
  /** The trot's offsets are the walk's: its diagonal pairs land apart. */
  trotUnpaired: false,
  /** The IK ignores the rest bend: knees take the other side. */
  poleFlipped: false,
  /** Poses never blend in. */
  poseIgnored: false,
  /** A swinging foot lands a whole stance's travel ahead instead of half: legs over-reach. */
  landFullStance: false,
  /** A three-segment leg's paw keeps its rest angle in the swing: no fold, the paw drags forward flat. */
  pawNoFold: false,
};

/** The leg ids, in the order the report lists them. */
export const QUAD_LEGS = ["f.l", "f.r", "h.l", "h.r"] as const;
export type QuadLeg = (typeof QUAD_LEGS)[number];

/** Each gait's phase offsets (a foot touches down when the clock passes its offset) and duty factor. */
const GAITS: Record<Exclude<QuadGait, "idle">, { off: Record<QuadLeg, number>; duty: number }> = {
  // the lateral sequence: left hind, left fore, right hind, right fore, a quarter cycle apart
  walk: { off: { "h.l": 0, "f.l": 0.25, "h.r": 0.5, "f.r": 0.75 }, duty: 0.7 },
  // diagonal pairs together
  trot: { off: { "f.l": 0, "h.r": 0, "f.r": 0.5, "h.l": 0.5 }, duty: 0.5 },
  // a transverse gallop: the hind pair, then the fore pair, each foot of a pair a little apart
  gallop: { off: { "h.l": 0, "h.r": 0.1, "f.l": 0.5, "f.r": 0.6 }, duty: 0.38 },
};
const PACE: Record<QuadLeg, number> = { "h.l": 0, "f.l": 0, "h.r": 0.5, "f.r": 0.5 };

/** What the motion reports each frame. */
export interface QuadReport {
  gait: QuadGait;
  /** Seconds in the gait. */
  time: number;
  /** Each foot: planted or not, its plants, its phase in the cycle, the rendered toe (`tip`), and where its hip, knee
   *  and ankle (the top of a three-segment leg's foot; the tip on a two-segment leg) are, all world space. */
  feet: { leg: QuadLeg; planted: boolean; plants: number; phase: number; tip: Vec3; hip: Vec3; knee: Vec3; ankle: Vec3 }[];
  /** Touchdowns, oldest first (the last 64): which foot, at which clock time (s). */
  touchdowns: { leg: QuadLeg; at: number }[];
  drift: DriftReport;
  /** Frames a foot's target lay past its leg's reach (on a three-segment leg, a planted foot's). */
  overstretch: number;
  /** The most the body came down to keep every leg in reach since the last `resetDrift` (m): a crouch on a slope or
   *  at full stride; on flat ground, a gait whose stride is too long for its legs. */
  reachDrop: number;
  /** Each knee's offset from its hip-to-foot line along the body's forward axis (m, + ahead), at this frame. */
  knees: Record<QuadLeg, number>;
  /** The poses' weights now (0 to 1). */
  poses: { eat: number; sit: number; lie: number };
  /** The body's height above the root (m) and pitch (radians, + nose up). */
  body: { height: number; pitch: number };
  look: { yaw: number; pitch: number };
}

/** A four-legged figure's motion. */
export interface QuadMotion {
  update(dt: number, input: QuadLocomotion): void;
  gait(): QuadGait;
  report(): QuadReport;
  /** Clear the drift record and the counters (start a measurement). */
  resetDrift(): void;
  /** Plant the feet under the hips and settle the chains (after a teleport). */
  reset(): void;
}

interface Foot {
  leg: QuadLeg;
  upper: THREE.Object3D;
  lower: THREE.Object3D;
  lowerMesh: THREE.Mesh | undefined;
  /** A three-segment leg's foot joint, its rest top (the ankle) and toe, its length, its rest angle in the leg's plane
   *  (radians from straight down, + toe ahead), its rest frame, and how far it peels and folds. */
  foot: THREE.Object3D | null;
  ankle0: THREE.Vector3;
  toe0: THREE.Vector3;
  c: number;
  ang0: number;
  footBasis0: THREE.Matrix4;
  peel: number;
  fold: number;
  /** Stance progress, 0 at touchdown to 1 at lift. */
  u: number;
  /** World points now, for the report. */
  kneeW: THREE.Vector3;
  ankleW: THREE.Vector3;
  hipW: THREE.Vector3;
  /** Pivot-space rest points: hip, knee, foot. */
  hip0: THREE.Vector3;
  knee0: THREE.Vector3;
  foot0: THREE.Vector3;
  a: number;
  b: number;
  /** Rest frames (pivot space) of the two segments: direction and bend side. */
  upBasis0: THREE.Matrix4;
  lowBasis0: THREE.Matrix4;
  /** The rest bend: the knee's side of the hip-to-foot line (pivot space, unit). */
  bend0: THREE.Vector3;
  planted: boolean;
  swinging: boolean;
  /** Swing progress 0 to 1 when the clock isn't driving it (stopping, settling). */
  s: number;
  phase: number;
  from: THREE.Vector3;
  anchor: THREE.Vector3;
  pos: THREE.Vector3;
  plants: number;
  tracking: number;
  start: THREE.Vector3;
  tip: THREE.Vector3;
  kneeOffset: number;
  /** Stepping out of turn (a foot about to run out of reach): its swing runs on its own timer. */
  early: boolean;
  /** How far from under its hip a foot may stand, flat ground, before its leg runs short (m). */
  maxOff: number;
}

const UP = new THREE.Vector3(0, 1, 0);
const clamp = (x: number, a: number, b: number): number => Math.min(b, Math.max(a, x));
const approach = (cur: number, target: number, rate: number, dt: number): number => target + (cur - target) * Math.exp(-rate * dt);
const wrap01 = (x: number): number => ((x % 1) + 1) % 1;
const _v = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _v3 = new THREE.Vector3();
const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _q2 = new THREE.Quaternion();

/** A frame from a direction turning in the leg's plane: x along it, z the body's left-right axis (pivot space). */
function sagittal(dir: THREE.Vector3, out: THREE.Matrix4): THREE.Matrix4 {
  const x = _v.copy(dir).normalize();
  const z = _v3.set(1, 0, 0).addScaledVector(x, -x.x).normalize();
  const y = _v2.crossVectors(z, x);
  return out.makeBasis(x, y, z);
}
const smooth = (a: number, b: number, x: number): number => {
  const t = clamp((x - a) / (b - a), 0, 1);
  return t * t * (3 - 2 * t);
};

/** A frame from a segment's direction and its bend side (both pivot space). */
function basis(dir: THREE.Vector3, side: THREE.Vector3, out: THREE.Matrix4): THREE.Matrix4 {
  const x = _v.copy(dir).normalize();
  const y = _v2.copy(side).addScaledVector(x, -side.dot(x));
  if (y.lengthSq() < 1e-10) y.set(0, 0, 1).addScaledVector(x, -x.z);
  y.normalize();
  const z = _v3.crossVectors(x, y);
  return out.makeBasis(x, y, z);
}

/**
 * The motion for a four-legged figure. `tuning` is the card's `quad`; `probe` finds the ground. Uses the joints
 * `body`, `head` (and `neck`), `tail`, and the legs `leg.<f|h>.<l|r>` with `shin.<f|h>.<l|r>` under them, whichever
 * the card has; a leg without its shin is left still.
 */
export function createQuadMotion(fig: Figure, tuning: QuadTuning, probe: GroundProbe): QuadMotion {
  const J = fig.joints;
  const body = J.get("body") ?? null;
  const neck = J.get("neck") ?? null;
  const head = J.get("head") ?? null;
  const tail = J.get("tail") ?? null;
  const bodyRest = body ? body.position.clone() : new THREE.Vector3();
  const bodyRest0 = body ? fig.rest.get("body")!.clone() : new THREE.Vector3();
  const fwd = new THREE.Vector3(0, 0, 1);
  const feet: Foot[] = [];
  for (const leg of QUAD_LEGS) {
    const upper = J.get(`leg.${leg}`);
    const lower = J.get(`shin.${leg}`);
    if (!upper || !lower) continue;
    const hip0 = fig.rest.get(`leg.${leg}`)!.clone();
    const knee0 = fig.rest.get(`shin.${leg}`)!.clone();
    const footJ = J.get(`foot.${leg}`) ?? null;
    const three = footJ !== null && fig.rest.has(`toe.${leg}`);
    const ankle0 = three ? fig.rest.get(`foot.${leg}`)!.clone() : new THREE.Vector3(hip0.x, 0, hip0.z);
    const toe0 = three ? fig.rest.get(`toe.${leg}`)!.clone() : ankle0.clone();
    // the toe plants; on a two-segment leg it is the point under the hip, on a three-segment one the card's toe
    const foot0 = new THREE.Vector3(toe0.x, 0, toe0.z);
    const toFoot = ankle0.clone().sub(hip0).normalize();
    // the knee's side of the hip-to-foot line, in the leg's plane (a straight leg bends forward for a fore leg, back
    // for a hind one)
    const bend0 = knee0.clone().sub(hip0);
    bend0.addScaledVector(toFoot, -bend0.dot(toFoot));
    if (bend0.lengthSq() < 1e-8) bend0.set(0, 0, leg.startsWith("f") ? 1 : -1);
    bend0.normalize();
    const fv = toe0.clone().sub(ankle0);
    const isFore = leg.startsWith("f");
    feet.push({
      leg,
      upper,
      lower,
      lowerMesh: fig.meshes.get(three ? `foot.${leg}` : `shin.${leg}`),
      foot: three ? footJ : null,
      ankle0,
      toe0,
      c: fv.length(),
      ang0: Math.atan2(fv.z, -fv.y),
      footBasis0: sagittal(fv.lengthSq() > 1e-10 ? fv : _v.set(0, -1, 0), new THREE.Matrix4()),
      peel: tuning.peel ?? 0.5,
      fold: (isFore ? tuning.fold?.fore : tuning.fold?.hind) ?? (isFore ? 1.8 : 0.7),
      u: 0.5,
      kneeW: new THREE.Vector3(),
      ankleW: new THREE.Vector3(),
      hipW: new THREE.Vector3(),
      hip0,
      knee0,
      foot0,
      a: knee0.distanceTo(hip0),
      b: ankle0.distanceTo(knee0),
      upBasis0: basis(knee0.clone().sub(hip0), bend0, new THREE.Matrix4()),
      lowBasis0: basis(ankle0.clone().sub(knee0), bend0, new THREE.Matrix4()),
      bend0,
      planted: false,
      swinging: false,
      s: 0,
      phase: 0,
      from: new THREE.Vector3(),
      anchor: new THREE.Vector3(),
      pos: new THREE.Vector3(),
      plants: 0,
      tracking: -1,
      start: new THREE.Vector3(),
      tip: new THREE.Vector3(),
      kneeOffset: 0,
      early: false,
      // how far the toe may stand from home before the leg runs short: a three-segment leg's foot turns to help it reach
      maxOff: three
        ? Math.sqrt(Math.max(0, (0.99 * (knee0.distanceTo(hip0) + ankle0.distanceTo(knee0) + toe0.distanceTo(ankle0))) ** 2 - hip0.y ** 2))
        : Math.sqrt(Math.max(0, (0.96 * (knee0.distanceTo(hip0) + ankle0.distanceTo(knee0))) ** 2 - hip0.y ** 2)),
    });
  }
  const fore = feet.filter((f) => f.leg.startsWith("f"));
  const hind = feet.filter((f) => f.leg.startsWith("h"));
  const span = fore.length && hind.length ? Math.abs(fore[0]!.hip0.z - hind[0]!.hip0.z) : 1;
  const bodyHeight = bodyRest0.y || 0.5;

  let gait: QuadGait = "idle";
  let gaitTime = 0;
  let clock = 0;
  let cycle = 0;
  const off: Record<QuadLeg, number> = { ...GAITS.walk.off };
  let duty = GAITS.walk.duty;
  let pitch = 0;
  let roll = 0;
  let lift = 0;
  let yaw = 0;
  let lookPitch = 0;
  let wag = 0;
  const pose = { eat: 0, sit: 0, lie: 0 };
  let bodyY = 0;
  /** The body lowered so every leg keeps its reach (a slope, a long stride), m. */
  let reachDrop = 0;
  let maxReachDrop = 0;
  let primed = false;
  let overstretch = 0;
  const lastRoot = new THREE.Vector3();
  let drift: DriftReport = { max: 0, plants: 0, samples: 0 };
  const touchdowns: QuadReport["touchdowns"] = [];

  const pivotToWorld = (p: THREE.Vector3, out: THREE.Vector3): THREE.Vector3 => fig.pivot.localToWorld(out.copy(p));
  const groundY = (x: number, z: number): number => probe(x, z, fig.root.position.y + 2) ?? fig.root.position.y;
  /** Where a foot rests now: under its hip as the root stands (world), on the probed ground. */
  const home = (f: Foot, out: THREE.Vector3): THREE.Vector3 => {
    pivotToWorld(f.foot0, out);
    return out;
  };
  const plantAt = (f: Foot, p: THREE.Vector3, clockTime: number): void => {
    f.anchor.set(p.x, QUAD_FAULTS.footProbeOff ? fig.root.position.y : groundY(p.x, p.z), p.z);
    f.pos.copy(f.anchor);
    f.planted = true;
    f.swinging = false;
    f.early = false;
    f.s = 0;
    f.plants++;
    touchdowns.push({ leg: f.leg, at: clockTime });
    if (touchdowns.length > 64) touchdowns.shift();
  };
  const startSwing = (f: Foot): void => {
    f.planted = false;
    f.swinging = true;
    f.s = 0;
    f.from.copy(f.pos);
  };

  const chooseGait = (sp: number): QuadGait => {
    const g = tuning.gallop;
    // hysteresis: up at a threshold, back down 15 % below it (40 % below walking's)
    if (gait === "gallop" && g !== undefined && sp > g * 0.85) return "gallop";
    if (g !== undefined && sp > g) return "gallop";
    if (gait === "trot" && sp > tuning.trot * 0.85) return "trot";
    if (sp > tuning.trot) return "trot";
    if (gait !== "idle" && sp > tuning.walk * 0.6) return "walk";
    if (sp > tuning.walk) return "walk";
    return "idle";
  };

  /** The gait's offsets and duty as configured, with the planted defects. */
  const target = (g: Exclude<QuadGait, "idle">): { off: Record<QuadLeg, number>; duty: number } => {
    const base = GAITS[g];
    const d = tuning.duty?.[g] ?? base.duty;
    if (g === "walk" && QUAD_FAULTS.walkPaced) return { off: PACE, duty: d };
    if (g === "trot" && QUAD_FAULTS.trotUnpaired) return { off: GAITS.walk.off, duty: d };
    return { off: base.off, duty: d };
  };

  const step = (inp: QuadLocomotion, dt: number): void => {
    const sp = inp.speed;
    const next = chooseGait(sp);
    if (next !== gait) {
      gait = next;
      gaitTime = 0;
    }
    gaitTime += dt;
    const vel = _vel.set(inp.velocity.x, 0, inp.velocity.z);
    if (QUAD_FAULTS.footProbeOff) for (const f of feet) if (f.planted) f.anchor.y = fig.root.position.y;
    if (gait === "idle") {
      // stopping: feet in the air finish their swing to their home and plant; standing, a foot far from its home steps
      for (const f of feet) {
        if (f.swinging) {
          f.s = Math.min(1, f.s + dt / Math.max(0.12, tuning.settle ?? 0.25));
          const land = home(f, _land);
          land.y = QUAD_FAULTS.footProbeOff ? fig.root.position.y : groundY(land.x, land.z);
          const e = f.s * f.s * (3 - 2 * f.s);
          f.pos.copy(f.from).lerp(land, e).addScaledVector(UP, tuning.stepHeight * 0.6 * Math.sin(Math.PI * f.s));
          if (f.s >= 1) plantAt(f, land, clock);
        }
      }
      if (!feet.some((f) => f.swinging)) {
        let far: Foot | null = null;
        let worst = Math.max(0.04, (tuning.stride.walk ?? 0.5) * 0.18);
        for (const f of feet) {
          const h = home(f, _home);
          const d = Math.hypot(f.anchor.x - h.x, f.anchor.z - h.z);
          if (d > worst) {
            worst = d;
            far = f;
          }
        }
        if (far) startSwing(far);
      }
      for (const f of feet) {
        if (f.planted) f.pos.copy(f.anchor);
        f.u = approach(f.u, 0.5, 6, dt);
      }
      lift = approach(lift, 0, 10, dt);
      return;
    }
    // the gait's offsets and duty ease toward the gait's (shortest way round the cycle)
    const t = target(gait);
    const k = 1 - Math.exp(-8 * dt);
    for (const leg of QUAD_LEGS) {
      let d = t.off[leg] - off[leg];
      d -= Math.round(d);
      off[leg] = wrap01(off[leg] + d * k);
    }
    duty += (t.duty - duty) * k;
    const stride = tuning.stride[gait] ?? tuning.stride.walk;
    const freq = Math.max(0.2, sp / Math.max(0.05, stride));
    cycle = wrap01(cycle + dt * freq);
    const stanceTime = duty / freq;
    lift = 0;
    const swingTime = (1 - duty) / freq;
    for (const f of feet) {
      const p = wrap01(cycle - off[f.leg]);
      f.phase = p;
      const inSwing = p >= duty;
      f.u = inSwing ? 1 : p / duty;
      // a planted foot about to run out of reach (setting off from a stand, a sudden turn) steps out of turn
      if (f.planted && !inSwing) {
        const h = home(f, _home);
        // a three-segment leg measures its true reach, from the hip (its toe's home may sit ahead of or behind it)
        const short = f.foot
          ? f.upper.getWorldPosition(_hipW).distanceTo(f.anchor) > (f.a + f.b + f.c) * 0.975
          : Math.hypot(f.anchor.x - h.x, f.anchor.z - h.z) > f.maxOff * 0.9;
        if (short) {
          startSwing(f);
          f.early = true;
        }
      }
      if (f.early) {
        f.s = Math.min(1, f.s + dt / Math.max(0.08, swingTime));
        const land = home(f, _land).addScaledVector(vel, (1 - f.s) * swingTime + stanceTime * 0.25);
        land.y = QUAD_FAULTS.footProbeOff ? fig.root.position.y : groundY(land.x, land.z);
        if (f.s >= 1) {
          plantAt(f, land, clock);
          continue;
        }
        const e = f.s * f.s * (3 - 2 * f.s);
        f.pos.copy(f.from).lerp(land, e).addScaledVector(UP, tuning.stepHeight * Math.sin(Math.PI * f.s));
        lift = Math.max(lift, Math.sin(Math.PI * f.s));
        continue;
      }
      if (inSwing && f.planted) startSwing(f);
      if (f.swinging) {
        const s = clamp((p - duty) / Math.max(1e-3, 1 - duty), 0, 1);
        const swingLeft = inSwing ? (1 - p) / freq : 0;
        // land where the hip will be at touchdown, plus half the stance's travel ahead
        const land = home(f, _land).addScaledVector(vel, swingLeft + stanceTime * (QUAD_FAULTS.landFullStance ? 1 : 0.5));
        land.y = QUAD_FAULTS.footProbeOff ? fig.root.position.y : groundY(land.x, land.z);
        if (!inSwing || s >= 1) {
          plantAt(f, land, clock);
          continue;
        }
        f.s = s;
        const e = s * s * (3 - 2 * s);
        // a three-segment leg lifts its paw high and early (the paw folding up under it) even at a walk, a hind paw less
        const h = f.foot
          ? tuning.stepHeight * clamp(sp / Math.max(1e-3, tuning.trot), 0.8, 1.2) * (f.leg.startsWith("h") ? 0.7 : 1)
          : tuning.stepHeight * clamp(sp / Math.max(1e-3, tuning.trot), 0.5, 1.2);
        f.pos.copy(f.from).lerp(land, e).addScaledVector(UP, h * Math.sin(Math.PI * (f.foot ? Math.pow(s, 0.75) : s)));
        lift = Math.max(lift, Math.sin(Math.PI * s));
      } else if (!f.planted) plantAt(f, f.pos, clock);
    }
    for (const f of feet) if (f.planted) f.pos.copy(f.anchor);
  };

  /** Two-bone IK for one leg: the knee on the rest bend's side, both segments turned with their rest twist. */
  const solveLeg = (f: Foot, bodyQ: THREE.Quaternion): void => {
    // pivot space throughout: the hip where the body carries it, the foot target, the bend carried by the body
    fig.pivot.updateWorldMatrix(true, false);
    const hip = f.upper.getWorldPosition(_hipW);
    f.hipW.copy(hip);
    fig.pivot.worldToLocal(hip);
    const tgt = fig.pivot.worldToLocal(_tgt.copy(f.pos));
    // a three-segment leg: the foot's angle from the step, its top (the ankle) is what the two upper segments reach
    let footVec: THREE.Vector3 | null = null;
    if (f.foot) {
      let ang = f.ang0;
      const moving = gait !== "idle";
      if (f.planted) ang -= moving ? f.peel * smooth(0.55, 1, f.u) : 0;
      else {
        // lifting: the peel eases out; the paw folds back through the swing's first part and straightens to land
        const sw = f.s;
        ang -= f.peel * (1 - smooth(0, 0.5, sw));
        if (!QUAD_FAULTS.pawNoFold) ang -= f.fold * Math.sin(Math.PI * clamp(sw / 0.8, 0, 1));
      }
      const dx = f.toe0.x - f.ankle0.x;
      const cs = Math.sqrt(Math.max(0, f.c * f.c - dx * dx));
      const ankleAt = (a: number, out: THREE.Vector3): THREE.Vector3 => out.set(tgt.x - dx, tgt.y + Math.cos(a) * cs, tgt.z - Math.sin(a) * cs);
      // where the upper segments can't reach the foot's top, the foot turns toward the hip until they can (a paw
      // flexing at full stride), as far as pointing straight at it
      const reach = (f.a + f.b) * 0.995;
      if (ankleAt(ang, _v).distanceTo(hip) > reach) {
        const toHip = Math.atan2(-(hip.z - tgt.z), hip.y - tgt.y);
        let lo = ang;
        let hi = toHip;
        if (ankleAt(hi, _v).distanceTo(hip) <= reach) {
          for (let i = 0; i < 16; i++) {
            const mid = (lo + hi) / 2;
            if (ankleAt(mid, _v).distanceTo(hip) > reach) lo = mid;
            else hi = mid;
          }
          ang = hi;
        } else ang = toHip;
      }
      footVec = _fv.set(dx, -Math.cos(ang) * cs, Math.sin(ang) * cs);
      tgt.sub(footVec);
    }
    const toT = _toT.copy(tgt).sub(hip);
    let d = toT.length();
    const max = (f.a + f.b) * 0.999;
    const min = Math.abs(f.a - f.b) + 1e-3;
    // a planted foot out of reach would slide; a three-segment leg's paw in the air just straightens toward its target
    if (d > max && (f.planted || !f.foot)) overstretch++;
    d = clamp(d, min, max);
    const u = toT.normalize();
    const pole = _pole.copy(f.bend0).applyQuaternion(bodyQ);
    if (QUAD_FAULTS.poleFlipped) pole.negate();
    const v = pole.addScaledVector(u, -pole.dot(u));
    if (v.lengthSq() < 1e-10) v.copy(f.bend0);
    v.normalize();
    const cosA = clamp((f.a * f.a + d * d - f.b * f.b) / (2 * f.a * d), -1, 1);
    const sinA = Math.sqrt(1 - cosA * cosA);
    const knee = _knee.copy(hip).addScaledVector(u, f.a * cosA).addScaledVector(v, f.a * sinA);
    const footP = _footP.copy(hip).addScaledVector(u, d);
    // the knee's offset along the body's forward axis from the hip-to-foot line (the report's knee check)
    const fwdNow = _v3.copy(fwd).applyQuaternion(bodyQ);
    const kh = _kh.copy(knee).sub(hip);
    f.kneeOffset = kh.addScaledVector(u, -kh.dot(u)).dot(fwdNow);
    // segment frames now vs at rest: R = B_now · B_rest⁻¹ (pivot space), then into each joint's parent's frame
    const upNow = basis(_v.copy(knee).sub(hip), v, _bUp);
    const qUp = _qUp.setFromRotationMatrix(_m.multiplyMatrices(upNow, _inv.copy(f.upBasis0).invert()));
    const lowNow = basis(_v.copy(footP).sub(knee), v, _bLow);
    const qLow = _qLow.setFromRotationMatrix(_m.multiplyMatrices(lowNow, _inv.copy(f.lowBasis0).invert()));
    const parentQ = parentPivotQ(f.upper, _q2);
    f.upper.quaternion.copy(parentQ.invert().multiply(qUp));
    f.lower.quaternion.copy(_q.copy(qUp).invert().multiply(qLow));
    if (f.foot && footVec) {
      const qFoot = _qFoot.setFromRotationMatrix(_m.multiplyMatrices(sagittal(footVec, _bFoot), _inv.copy(f.footBasis0).invert()));
      f.foot.quaternion.copy(_q.copy(qLow).invert().multiply(qFoot));
    }
    f.kneeW.copy(knee);
    fig.pivot.localToWorld(f.kneeW);
    f.ankleW.copy(footP);
    fig.pivot.localToWorld(f.ankleW);
  };
  /** A joint's parent's orientation in pivot space. */
  const parentPivotQ = (joint: THREE.Object3D, out: THREE.Quaternion): THREE.Quaternion => {
    out.identity();
    for (let p = joint.parent; p && p !== fig.pivot; p = p.parent) out.premultiply(p.quaternion);
    return out;
  };

  const poseBody = (inp: QuadLocomotion, dt: number): THREE.Quaternion => {
    const sp = inp.speed;
    // the ground under the fore and hind hips: pitch, and the body's height over the root
    let yF = fig.root.position.y;
    let yH = fig.root.position.y;
    if (fore.length && hind.length) {
      const pf = pivotToWorld(_v.set(0, 0, fore[0]!.hip0.z), _v);
      yF = groundY(pf.x, pf.z);
      const ph = pivotToWorld(_v2.set(0, 0, hind[0]!.hip0.z), _v2);
      yH = groundY(ph.x, ph.z);
    }
    const slope = Math.atan2(yF - yH, span);
    // poses at rest only
    const still = gait === "idle";
    const want = (p: "eat" | "sit" | "lie"): number => (still && !QUAD_FAULTS.poseIgnored && inp.pose === p && (p !== "sit" || tuning.sit !== undefined) ? 1 : 0);
    for (const p of ["eat", "sit", "lie"] as const) pose[p] = approach(pose[p], want(p), 4, dt);
    const sitPitch = (tuning.sit ?? 0) * pose.sit;
    pitch = approach(pitch, slope + sitPitch, 8, dt);
    // a grazer dips its fore end a little as it reaches for the grass
    const eatDip = 0.15 * pose.eat;
    // a sit lowers the hind end: the body drops by half the hind hips' fall; a lie drops it toward its lie height
    const sitDrop = Math.sin(sitPitch) * span * 0.5;
    const lieDrop = (1 - (tuning.lie ?? 0.4)) * bodyHeight * pose.lie;
    const gaitBob = gait === "idle" ? 0 : tuning.bob * Math.sin(cycle * Math.PI * 4) * clamp(sp / Math.max(tuning.trot, 1e-3), 0.3, 1.5);
    const breath = gait === "idle" ? tuning.breath * Math.sin(clock * 2.2) : 0;
    bodyY = approach(bodyY, (yF + yH) * 0.5 - fig.root.position.y, 12, dt);
    // legs keep their reach: where a foot's target lies past 96 % of its leg, the body comes down by the difference
    // (as an animal crouches on a slope or at full stride), and rises back slowly
    let need = 0;
    for (const f of feet) {
      const hip = f.upper.getWorldPosition(_hipW);
      const dx = f.pos.x - hip.x;
      const dz = f.pos.z - hip.z;
      // a three-segment leg reaches its toe with its foot turned toward the hip at most; a two-segment one its foot
      const reach = f.foot ? (f.a + f.b + f.c) * 0.99 : (f.a + f.b) * 0.96;
      const flat2 = dx * dx + dz * dz;
      const vertical = hip.y + reachDrop - f.pos.y;
      if (flat2 < reach * reach) need = Math.max(need, vertical - Math.sqrt(reach * reach - flat2));
      else need = Math.max(need, vertical);
    }
    reachDrop = need > reachDrop ? need : approach(reachDrop, need, 3, dt);
    maxReachDrop = Math.max(maxReachDrop, reachDrop);
    roll = approach(roll, 0, 8, dt);
    // the gallop's rocking eases in over its first 0.3 s, so a foot planted at full reach in the trot isn't lifted out
    const gallopPitch = gait === "gallop" ? (tuning.pitchBob ?? 0.06) * Math.sin(cycle * Math.PI * 2) * Math.min(1, gaitTime / 0.3) : 0;
    if (body) {
      body.position.copy(bodyRest).add(_v.set(0, bodyY + gaitBob + breath - sitDrop - lieDrop - reachDrop, 0));
      body.rotation.set(-(pitch + gallopPitch) + eatDip, 0, roll, "YXZ");
    }
    // a lie folds the legs under the body: the feet draw in toward its middle
    if (pose.lie > 0.001 && gait === "idle")
      for (const f of feet) {
        const mid = pivotToWorld(_v.set(f.foot0.x * 0.8, 0, f.foot0.z * 0.55), _v);
        f.pos.lerpVectors(f.anchor, _v2.set(mid.x, f.anchor.y, mid.z), pose.lie);
      }
    if (pose.sit > 0.001 && gait === "idle")
      for (const f of hind) {
        const tuck = pivotToWorld(_v.set(f.foot0.x, 0, f.foot0.z + span * 0.35), _v);
        f.pos.lerpVectors(f.anchor, _v2.set(tuck.x, f.anchor.y, tuck.z), pose.sit);
      }
    const q = _bodyQ.setFromEuler(new THREE.Euler(-(pitch + gallopPitch) + eatDip, 0, roll, "YXZ"));
    return q;
  };

  const poseHead = (inp: QuadLocomotion, dt: number): void => {
    if (!head) return;
    let ty = 0;
    let tp = 0;
    if (inp.lookAt) {
      head.parent!.updateWorldMatrix(true, false);
      const local = head.parent!.worldToLocal(_v.set(inp.lookAt.x, inp.lookAt.y, inp.lookAt.z)).sub(head.position);
      const flat = Math.hypot(local.x, local.z);
      if (flat > 0.3) {
        ty = Math.atan2(local.x, local.z);
        tp = Math.atan2(local.y, flat);
      }
    }
    ty = clamp(ty, -tuning.look.yaw, tuning.look.yaw);
    tp = clamp(tp, -tuning.look.down, tuning.look.up);
    yaw = approach(yaw, ty, tuning.look.rate, dt);
    lookPitch = approach(lookPitch, tp, tuning.look.rate, dt);
    // eating bends the neck (or the head) down; a gait nods the head against the body's bob
    const eat = (tuning.eat ?? 0.9) * pose.eat;
    const nod = gait === "walk" ? 0.06 * Math.sin(cycle * Math.PI * 4 + 1) : 0;
    if (neck) {
      neck.rotation.set(eat * 0.7 + pitch * 0.5, yaw * 0.4, 0, "YXZ");
      head.rotation.set(-lookPitch + eat * 0.3 + nod, yaw * 0.6, 0, "YXZ");
    } else head.rotation.set(-lookPitch + eat + nod + pitch * 0.5, yaw, 0, "YXZ");
  };

  const poseTail = (inp: QuadLocomotion, dt: number): void => {
    if (!tail) return;
    wag = approach(wag, inp.wag ? 1 : 0, 6, dt);
    const w = tuning.wag;
    tail.rotation.y = w ? w.amp * wag * Math.sin(clock * w.rate * Math.PI * 2) : 0;
  };

  const measure = (): void => {
    fig.root.updateMatrixWorld(true);
    for (const f of feet) {
      if (f.foot) {
        // the rendered toe: the foot's rest toe carried by its joint
        f.foot.updateWorldMatrix(true, false);
        f.tip.copy(f.toe0).sub(f.ankle0).applyMatrix4(f.foot.matrixWorld);
      } else if (f.lowerMesh) {
        // the rendered foot: the lower segment's rest foot point carried by its joint
        f.lower.updateWorldMatrix(true, false);
        f.tip.copy(f.foot0).sub(f.knee0).applyMatrix4(f.lower.matrixWorld);
      } else f.tip.copy(f.pos);
      if (!f.planted) continue;
      if (f.tracking !== f.plants) {
        f.tracking = f.plants;
        f.start.copy(f.tip);
        drift.plants++;
        continue;
      }
      drift.samples++;
      drift.max = Math.max(drift.max, f.tip.distanceTo(f.start));
    }
  };

  const solveAll = (q: THREE.Quaternion): void => {
    fig.root.updateMatrixWorld(true);
    for (const f of feet) solveLeg(f, q);
  };

  const reset = (): void => {
    fig.root.updateMatrixWorld(true);
    for (const f of feet) plantAt(f, home(f, _home), clock);
    touchdowns.length = 0;
    solveAll(_bodyQ.identity());
    fig.root.updateMatrixWorld(true);
    for (const c of fig.chains.values()) c.reset();
    primed = true;
  };

  const motion: QuadMotion = {
    update(dtIn, inp) {
      const dt = Math.min(0.1, Math.max(0, dtIn));
      clock += dt;
      if (!primed || lastRoot.distanceTo(fig.root.position) > 2) reset();
      lastRoot.copy(fig.root.position);
      fig.root.updateMatrixWorld(true);
      step(inp, dt);
      const q = poseBody(inp, dt);
      solveAll(q);
      poseHead(inp, dt);
      poseTail(inp, dt);
      measure();
      for (const c of fig.chains.values()) c.update(dt);
    },
    gait: () => gait,
    report: () => ({
      gait,
      time: gaitTime,
      feet: feet.map((f) => ({ leg: f.leg, planted: f.planted, plants: f.plants, phase: f.phase, tip: f.tip.toArray() as Vec3, hip: f.hipW.toArray() as Vec3, knee: f.kneeW.toArray() as Vec3, ankle: (f.foot ? f.ankleW : f.tip).toArray() as Vec3 })),
      touchdowns: touchdowns.map((t) => ({ ...t })),
      drift: { ...drift },
      overstretch,
      reachDrop: maxReachDrop,
      knees: Object.fromEntries(feet.map((f) => [f.leg, f.kneeOffset])) as Record<QuadLeg, number>,
      poses: { ...pose },
      body: { height: body ? body.position.y - bodyRest.y + bodyRest0.y : 0, pitch },
      look: { yaw, pitch: lookPitch },
    }),
    resetDrift() {
      drift = { max: 0, plants: 0, samples: 0 };
      overstretch = 0;
      maxReachDrop = 0;
      touchdowns.length = 0;
      for (const f of feet) f.tracking = -1;
    },
    reset,
  };
  return motion;
}

const _vel = new THREE.Vector3();
const _land = new THREE.Vector3();
const _home = new THREE.Vector3();
const _hipW = new THREE.Vector3();
const _tgt = new THREE.Vector3();
const _toT = new THREE.Vector3();
const _pole = new THREE.Vector3();
const _knee = new THREE.Vector3();
const _footP = new THREE.Vector3();
const _kh = new THREE.Vector3();
const _bUp = new THREE.Matrix4();
const _bLow = new THREE.Matrix4();
const _inv = new THREE.Matrix4();
const _qUp = new THREE.Quaternion();
const _qLow = new THREE.Quaternion();
const _bodyQ = new THREE.Quaternion();
const _fv = new THREE.Vector3();
const _bFoot = new THREE.Matrix4();
const _qFoot = new THREE.Quaternion();
